import { FC, useEffect, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Send } from "lucide-react";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

import Quill from "quill";
import Link from "quill/formats/link";
class CustomLink extends Link {
  static sanitize(url: string) {
    let sanitizedUrl = super.sanitize(url);
    if (sanitizedUrl && !sanitizedUrl.startsWith("http")) {
      sanitizedUrl = "https://" + sanitizedUrl;
    }
    return sanitizedUrl;
  }
}

Quill.register(CustomLink, true);
interface EditorProps {
  className: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  buttonMessage?: string;
  buttonLoadingMessage?: string;
  isQuillEditing?: boolean;
  autoFocus?: boolean;
}

const CustomQuillEditor: FC<EditorProps> = ({
  placeholder,
  className,
  value,
  onChange,
  onSubmit,
  onCancel,
  disabled,
  buttonMessage,
  buttonLoadingMessage,
  isQuillEditing,
  autoFocus,
}) => {
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .quill-container .ql-editor {
        max-height: 200px;
        overflow-y: auto;
      }
      
      /* Fix the toolbar position */
      .quill-container .ql-toolbar {
        position: sticky;
        top: 0;
        z-index: 10;
      }
  
      /* Ensure tooltip appears in correct position */
      .ql-container {
        position: relative;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!autoFocus) return;
    if (disabled) return;

    // Ensure the editor is mounted before focusing.
    const timeoutId = window.setTimeout(() => {
      const editor = quillRef.current?.getEditor?.();
      editor?.focus();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [autoFocus, disabled]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "indent",
    "link",
    "image",
  ];
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Check for Command+Enter (Mac) or Control+Enter (Windows)
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      if (
        onSubmit &&
        !disabled &&
        value.length > 0 &&
        value !== "<p><br></p>"
      ) {
        onSubmit();
      }
    }
  };
  const isMac = navigator.userAgent.toUpperCase().indexOf("MAC") >= 0;
  return (
    <div
      className={cn(
        "quill-container !relative",
        isQuillEditing && " quill-is-editing"
      )}
    >
      <ReactQuill
        ref={quillRef}
        className={className}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
      />
      <div className="!flex !items-center !gap-2 !ml-auto !absolute !top-1.5 !right-1.5 z-10">
        {onSubmit && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                asChild
                disabled={
                  disabled || value.length === 0 || value === "<p><br></p>"
                }
                className={cn(
                  "!flex !row-reverse !bg-primary !text-primary-foreground !w-fit !px-2",
                  (disabled || value.length === 0 || value === "<p><br></p>") &&
                    "!cursor-not-allowed !pointer-events-auto"
                )}
              >
                <Button
                  onClick={onSubmit}
                  disabled={
                    disabled || value.length === 0 || value === "<p><br></p>"
                  }
                  className={cn(
                    "!flex !row-reverse !bg-primary !text-primary-foreground !w-fit !px-2",
                    (disabled ||
                      value.length === 0 ||
                      value === "<p><br></p>") &&
                      "!cursor-not-allowed !pointer-events-auto"
                  )}
                  size="sm"
                >
                  {disabled ? buttonLoadingMessage : buttonMessage || "Send"}
                  {disabled ? (
                    <Spinner className="size-4 animate-spin !text-primary-foreground" />
                  ) : (
                    <Send className="!size-3 [&>path]:stroke-primary-foreground [&>path]:hover:stroke-primary-foreground" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="shadow-md">
                <p>
                  {buttonMessage || "Send"} ({isMac ? "⌘" : "Ctrl"} + Enter)
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outline"
            size="sm"
            className="!w-fit !px-2 !ml-auto !text-xs"
          >
            Annuller
          </Button>
        )}
      </div>
    </div>
  );
};

export default CustomQuillEditor;
